/**
 * 用量反応（LD50 / EC50 / EC90 / TCID50）の UI デフォルトと手法制約
 */

import type { AnalysisOptions } from '../stores/systems/keisuke/dashboardStore';
import type { Method } from './statistics/doseResponse';

export type DrTypeKey = 'ld50' | 'ec50' | 'ec90' | 'tcid50';

export const DR_TYPES: DrTypeKey[] = ['ld50', 'ec50', 'ec90', 'tcid50'];

export function normalizeDrType(raw: unknown): DrTypeKey {
  if (raw === 'ec50' || raw === 'ec90' || raw === 'tcid50' || raw === 'ld50') return raw;
  return 'ld50';
}

/** 指標ごとに選択可能な計算手法 */
export function getAllowedDrMethods(drType: DrTypeKey): Method[] {
  if (drType === 'tcid50') return ['reed-muench', 'spearman-karber'];
  return ['probit', 'logistic', 'reed-muench', 'spearman-karber'];
}

/** 指標と現在値から有効な手法へ正規化 */
export function normalizeDrMethodForType(drType: DrTypeKey, method: unknown): Method {
  const allowed = getAllowedDrMethods(drType);
  if (typeof method === 'string' && (allowed as string[]).includes(method)) {
    return method as Method;
  }
  return drType === 'tcid50' ? 'reed-muench' : 'probit';
}

/** Probit/Logistic では総数列が必須 */
export function drMethodRequiresTotalColumn(method: Method): boolean {
  return method === 'probit' || method === 'logistic';
}

/** TCID50 では総数列が必須 */
export function drTypeRequiresTotalColumn(drType: DrTypeKey): boolean {
  return drType === 'tcid50';
}

/** 近似区間を返す手法（正式な 95% CI ではない） */
export function drMethodUsesApproximateInterval(method: Method): boolean {
  return method === 'reed-muench' || method === 'spearman-karber';
}

/** 指標切替時に analysisOptions へマージするパッチ */
export function buildDrTypeSwitchPatch(
  drType: DrTypeKey,
  current: AnalysisOptions
): Partial<AnalysisOptions> {
  const drMethod = normalizeDrMethodForType(drType, current.drMethod);
  const mapping = { ...(current.mapping ?? {}) };
  if (!mapping.doseColumn?.trim()) mapping.doseColumn = 'Dose';
  if (!mapping.effectColumn?.trim()) {
    mapping.effectColumn = drType === 'tcid50' ? 'Infected' : 'Effect';
  }
  if (drTypeRequiresTotalColumn(drType) || drMethodRequiresTotalColumn(drMethod)) {
    if (!mapping.totalColumn?.trim()) {
      mapping.totalColumn = drType === 'tcid50' ? 'Total' : 'Total';
    }
  }
  return { drType, drMethod, mapping };
}

/** 保存復元・ストア更新時に drType/drMethod/mapping を整合させる */
export function finalizeDoseResponseOptions(options: AnalysisOptions): AnalysisOptions {
  if (options.type !== 'dose-response') return options;
  return { ...options, ...buildDrTypeSwitchPatch(normalizeDrType(options.drType), options) };
}

/** TCID50 プレート1サンプルをワークスペース表へ載せるときの既定ヘッダー */
export const TCID50_WORKSPACE_HEADERS = ['Dilution', 'Infected', 'Total'] as const;
